import { type FieldValues, type UseFormSetError } from 'react-hook-form';
import { toast } from 'react-toastify';

import * as Sentry from '@sentry/react';
import { type AnyObjectSchema } from 'yup';

import { upperSnakeCaseToCamelCase } from '../helpers/textTransforms';
import i18n from '../i18n/i18n';

interface ResponseError {
  constraint_error?: string;
  identifier?: string;
  message: string;
  property?: string;
  property_constraint_error?: string;
  code?: number;
}

const errorMessage = ({ constraint_error, message, property, identifier, code }: ResponseError): string => {
  if (!constraint_error && !identifier) return message;

  const propertyName = i18n.t(`namespaces:properties:${property}`, {
    defaultValue: property,
  });

  let errorMessage;

  if (identifier) {
    errorMessage = i18n.t(`namespaces:errors:${upperSnakeCaseToCamelCase(identifier ?? constraint_error)}`, {
      defaultValue: null,
      name: propertyName,
    });
  }

  if (constraint_error && !errorMessage) {
    errorMessage = i18n.t(`namespaces:errors:${upperSnakeCaseToCamelCase(constraint_error)}`, {
      defaultValue: null,
      name: propertyName,
    });
  }

  if (code && !errorMessage) {
    errorMessage = i18n.t(`namespaces:errors:${code}`, {
      defaultValue: null,
    });
  }

  return errorMessage ?? message;
};

const notifyErrors = (errors: ResponseError[]) => {
  if (errors.length === 1) {
    toast.error(
      <span>
        {errors[0]?.property ? <strong>{errors[0].property}: </strong> : null}
        {errorMessage(errors[0])}
      </span>,
    );
  } else if (errors.length > 1) {
    toast.error(
      <ul>
        {errors.map((error, index) => (
          <li key={index}>
            {error?.property ? <strong>{error.property}: </strong> : null}
            {errorMessage(error)}
          </li>
        ))}
      </ul>,
    );
  }
};

type HandleBadResponse = <T extends FieldValues>(
  response: Response,
  schema?: AnyObjectSchema,
  setError?: UseFormSetError<T>,
) => Promise<void>;

const handleBadResponse: HandleBadResponse = async (response, schema, setError) => {
  let json;

  try {
    json = (await response.json()) as ResponseError | ResponseError[];

    const errors = Array.isArray(json) ? json : [json];

    // Show all errors using toast if no schema or setError is provided
    if (!schema || !setError) return notifyErrors(errors);

    // Split errors into field errors and other errors
    const fieldNames = Object.keys(schema.fields);

    const { fieldErrors, otherErrors } = errors.reduce(
      (acc, cur) => {
        if (cur.property && fieldNames && fieldNames.includes(cur.property)) acc.fieldErrors.push(cur);
        else acc.otherErrors.push(cur);

        return acc;
      },
      {
        fieldErrors: [] as ResponseError[],
        otherErrors: [] as ResponseError[],
      },
    );

    // Set errors for fields using react-hook-form setError
    fieldErrors.forEach((error) => {
      if (error.property) {
        // @ts-expect-error - setError can have different formvalues
        setError(error.property, {
          type: error.identifier,
          message: errorMessage(error),
        });
      }
    });

    // Notify other errors using toast
    notifyErrors(otherErrors);
  } catch (e) {
    notifyErrors([{ message: 'Something went wrong. Please try again.' }]);

    const scope = new Sentry.Scope();
    scope.setExtra('response', response);
    scope.setExtra('json', json);

    Sentry.captureException(e, scope);
  }
};

export default handleBadResponse;
